import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { API_BASE_URL } from '../../../config/api';
import { documentService, getErrorMessage, vendorService } from '../../../services/apiService';
import { useToast } from '../../../components/ui/Toast';

const DocumentsTab = ({ vendor, userRole }) => {
  const toast = useToast();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [documents, setDocuments] = useState([]);
  const [documentStats, setDocumentStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    expired: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [regeneratingMsme, setRegeneratingMsme] = useState(false);

  // Document type mapping for categories
  const documentTypeCategories = {
    registration: ['pan_card', 'company_registration', 'incorporation_certificate', 'msme_certificate', 'msme_declaration'],
    financial: ['bank_statement', 'tax_certificate'],
    compliance: ['gst_certificate', 'business_license', 'insurance_certificate'],
    agreements: ['quality_certificate', 'other']
  };
  const normalizeDocumentType = (docType) => String(docType || '').toLowerCase();

  // Fetch documents and stats
  useEffect(() => {
    if (vendor?.id) {
      fetchDocuments();
      fetchDocumentStats();
    }
  }, [vendor?.id]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await documentService.getVendorDocuments(vendor.id);
      
      // Handle different response structures
      const documentsData = Array.isArray(response) ? response : (response?.data || response?.documents || []);
      setDocuments(documentsData);
    } catch (err) {
      console.error('Error fetching documents:', err);
      const errorMsg = getErrorMessage(err) || 'Failed to load documents. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/stats/vendor/${vendor.id}`, {
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Handle different response structures
      const statsData = data?.data || data;
      setDocumentStats({
        total: statsData?.total || 0,
        pending: statsData?.pending || 0,
        approved: statsData?.approved || 0,
        rejected: statsData?.rejected || 0,
        expired: statsData?.expired || 0,
      });
    } catch (err) {
      console.error('Error fetching document stats:', err);
      // Don't set error state for stats - it's not critical
    }
  };

  // Calculate category counts
  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') {
      return documents.length;
    }
    
    const categoryTypes = (documentTypeCategories[categoryId] || []).map(normalizeDocumentType);
    return documents.filter(doc => categoryTypes.includes(normalizeDocumentType(doc.document_type))).length;
  };

  const documentCategories = [
    { id: 'all', label: 'All Documents', count: getCategoryCount('all') },
    { id: 'registration', label: 'Registration', count: getCategoryCount('registration') },
    { id: 'financial', label: 'Financial', count: getCategoryCount('financial') },
    { id: 'compliance', label: 'Compliance', count: getCategoryCount('compliance') },
    { id: 'agreements', label: 'Agreements', count: getCategoryCount('agreements') }
  ];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-emerald-100 text-emerald-800';
      case 'pending':
        return 'bg-amber-100 text-amber-800';
      case 'rejected':
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return 'FileText';
    if (mimeType?.includes('image')) return 'Image';
    if (mimeType?.includes('document')) return 'FileText';
    return 'File';
  };

  // Human-friendly document type labels
  const formatDocumentType = (docType) => {
    if (!docType) return 'Document';
    const normalizedDocType = normalizeDocumentType(docType);
    const map = {
      gst_certificate: 'GST Certificate',
      pan_card: 'PAN Card',
      bank_statement: 'Bank Statement',
      msme_certificate: 'MSME Certificate',
      msme_declaration: 'MSME Declaration',
      company_registration: 'Company Registration',
      incorporation_certificate: 'Incorporation Certificate',
      business_license: 'Business License',
      insurance_certificate: 'Insurance Certificate',
      quality_certificate: 'Quality Certificate',
      tax_certificate: 'Tax Certificate',
      other: 'Other Document'
    };
    if (map[normalizedDocType]) return map[normalizedDocType];
    // Fallback: titleize with spaces
    return normalizedDocType
      .split('_')
      .map(w => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
      .join(' ');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const filteredDocuments = selectedCategory === 'all' 
    ? documents 
    : documents.filter(doc => {
        const categoryTypes = (documentTypeCategories[selectedCategory] || []).map(normalizeDocumentType);
        return categoryTypes.includes(normalizeDocumentType(doc.document_type));
      });
  const hasMsmeDeclaration = documents.some(
    (doc) => normalizeDocumentType(doc.document_type) === 'msme_declaration'
  );
  const canRegenerateMsme = userRole === 'Admin' || userRole === 'Approver';

  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays > 0;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    return expiry < today;
  };

  const handleRegenerateMsmeDeclaration = async () => {
    try {
      setRegeneratingMsme(true);
      await vendorService.regenerateMsmeDeclaration(vendor.id);
      toast.success('MSME declaration regeneration started.');
      await fetchDocuments();
      await fetchDocumentStats();
    } catch (err) {
      console.error('Error regenerating MSME declaration:', err);
      toast.error(getErrorMessage(err) || 'Failed to regenerate MSME declaration.');
    } finally {
      setRegeneratingMsme(false);
    }
  };

  const handleUpload = async (formData) => {
    try {
      setUploading(true);
      const response = await fetch(`${API_BASE_URL}/documents/vendor/${vendor.id}/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      // Refresh documents and stats
      await fetchDocuments();
      await fetchDocumentStats();
      setUploadModalOpen(false);
      toast.success('Document uploaded successfully!');
    } catch (err) {
      console.error('Error uploading document:', err);
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      // Try authenticated endpoint first
      try {
        const response = await documentService.downloadDocument(documentId);
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `document-${documentId}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (authError) {
        // Fallback to public endpoint if authenticated fails
        const response = await fetch(`${API_BASE_URL}/documents/${documentId}/public/download`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Download failed: ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `document-${documentId}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Error downloading document:', err);
      toast.error(`Download failed: ${err.message || 'Please try again.'}`);
    }
  };

  const handlePreview = async (documentId) => {
    try {
      // Try authenticated endpoint first
      let blob;
      try {
        const response = await documentService.downloadDocument(documentId);
        blob = response.data;
      } catch (authError) {
        // Fallback to public endpoint if authenticated fails
        const response = await fetch(`${API_BASE_URL}/documents/${documentId}/public/download?inline=true`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Preview failed: ${response.status}`);
        }

        blob = await response.blob();
      }

      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Error previewing document:', err);
      toast.error(`Preview failed: ${err.message || 'Please try again.'}`);
    }
  };

  if (loading) {
    return (
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto mb-4"></div>
              <p className="text-slate-600">Loading documents...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="text-center py-12">
            <Icon name="AlertCircle" size={64} className="text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Error Loading Documents</h3>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button variant="default" onClick={fetchDocuments}>
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* Document Categories */}
      <div className="flex flex-wrap gap-2">
        {documentCategories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedCategory === category.id
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
            }`}
          >
            <span>{category.label}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              selectedCategory === category.id
                ? 'bg-white/20 text-white'
                : 'bg-slate-200 text-slate-600'
            }`}>
              {category.count}
            </span>
          </button>
        ))}
      </div>

      {/* <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"> */}
            {/* <div>
              <h3 className="text-lg font-semibold text-slate-900">MSME Declaration</h3>
              <p className="text-sm text-slate-600 mt-1">
                {hasMsmeDeclaration
                  ? 'MSME declaration is available in the documents list.'
                  : 'No MSME declaration document is available yet.'}
              </p>
            </div>
            {canRegenerateMsme && (
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={handleRegenerateMsmeDeclaration}
                disabled={regeneratingMsme}
              >
                {regeneratingMsme ? 'Regenerating...' : 'Regenerate MSME Declaration'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card> */}

      {/* Upload New Document */}
      {(userRole === 'Admin' || userRole === 'Approver') && (
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Upload New Document</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Add new documents or update existing ones
                </p>
              </div>
            <Button 
              variant="default" 
              iconName="Upload" 
              iconPosition="left"
              onClick={() => setUploadModalOpen(true)}
            >
              Upload Document
            </Button>
          </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md border border-slate-200 shadow-sm bg-white">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Upload Document</h3>
            <UploadForm 
              onSubmit={handleUpload} 
              onCancel={() => setUploadModalOpen(false)}
              uploading={uploading}
            />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Documents Grid */}
      {filteredDocuments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredDocuments.map((document) => (
            <Card key={document.id} className="border border-slate-200 shadow-sm bg-white">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Icon name={getFileIcon(document.mime_type)} size={24} className="text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 truncate max-w-full">
                        {formatDocumentType(document.document_type)}
                      </h4>
                      <p className="text-sm text-slate-600">
                        {document.mime_type?.split('/')[1]?.toUpperCase()} • {formatFileSize(document.file_size)}
                      </p>
                    </div>
                  </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor('approved')}`}>
                    Approved
                  </span>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Type:</span>
                  <span className="text-slate-900">{formatDocumentType(document.document_type)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Uploaded:</span>
                  <span className="text-slate-900">{formatDate(document.created_at)}</span>
                </div>
                {document.expiry_date && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Expires:</span>
                    <span className={`font-medium ${
                      isExpired(document.expiry_date) ? 'text-red-700' :
                      isExpiringSoon(document.expiry_date) ? 'text-amber-700' : 'text-slate-900'
                    }`}>
                      {formatDate(document.expiry_date)}
                      {isExpiringSoon(document.expiry_date) && !isExpired(document.expiry_date) && (
                        <span className="ml-1 text-xs">(Soon)</span>
                      )}
                      {isExpired(document.expiry_date) && (
                        <span className="ml-1 text-xs">(Expired)</span>
                      )}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Eye"
                  iconPosition="left"
                  className="flex-1 min-w-0"
                  onClick={() => handlePreview(document.id)}
                >
                  Preview
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  className="flex-1 min-w-0"
                  onClick={() => handleDownload(document.id, document.file_name)}
                >
                  Download
                </Button>
                {canRegenerateMsme &&
                  normalizeDocumentType(document.document_type) === 'msme_declaration' && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="RefreshCw"
                      iconPosition="left"
                      className="flex-1 min-w-0"
                      onClick={handleRegenerateMsmeDeclaration}
                      disabled={regeneratingMsme}
                    >
                      {regeneratingMsme ? 'Regenerating...' : 'Regenerate'}
                    </Button>
                  )}
              </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border border-slate-200 shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="text-center py-12">
              <Icon name="FileText" size={64} className="text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Documents Available</h3>
              <p className="text-slate-600 mb-6">
            {selectedCategory === 'all' 
              ? "This vendor hasn't uploaded any documents yet."
              : `No documents found in the ${selectedCategory} category.`
            }
          </p>
          {(userRole === 'Admin' || userRole === 'Approver') && (
            <Button 
              variant="default" 
              iconName="Upload" 
              iconPosition="left"
              onClick={() => setUploadModalOpen(true)}
            >
              Upload First Document
            </Button>
          )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Summary */}
      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Icon name="BarChart3" size={20} className="text-slate-600" />
            Document Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="text-center">
              <div className="text-2xl font-semibold text-slate-900 mb-1">{documentStats.total}</div>
              <div className="text-sm text-slate-600">Total Documents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-emerald-700 mb-1">{documentStats.approved}</div>
              <div className="text-sm text-slate-600">Approved</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-amber-700 mb-1">{documentStats.pending}</div>
              <div className="text-sm text-slate-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-red-700 mb-1">{documentStats.rejected}</div>
              <div className="text-sm text-slate-600">Rejected</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-red-700 mb-1">{documentStats.expired}</div>
              <div className="text-sm text-slate-600">Expired</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Upload Form Component
const UploadForm = ({ onSubmit, onCancel, uploading }) => {
  const [formData, setFormData] = useState({
    document_type: '',
    file: null,
    expiry_date: ''
  });

  const documentTypes = [
    { value: 'gst_certificate', label: 'GST Certificate' },
    { value: 'pan_card', label: 'PAN Card' },
    { value: 'bank_statement', label: 'Bank Statement' },
    { value: 'msme_certificate', label: 'MSME Certificate' },
    { value: 'company_registration', label: 'Company Registration' },
    { value: 'incorporation_certificate', label: 'Incorporation Certificate' },
    { value: 'business_license', label: 'Business License' },
    { value: 'insurance_certificate', label: 'Insurance Certificate' },
    { value: 'quality_certificate', label: 'Quality Certificate' },
    { value: 'tax_certificate', label: 'Tax Certificate' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.document_type || !formData.file) {
      toast.warning('Please select document type and file');
      return;
    }

    const data = new FormData();
    data.append('document_type', formData.document_type);
    data.append('file', formData.file);
    if (formData.expiry_date) {
      data.append('expiry_date', formData.expiry_date);
    }

    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-900 mb-2">
          Document Type *
        </label>
        <select
          value={formData.document_type}
          onChange={(e) => setFormData(prev => ({ ...prev, document_type: e.target.value }))}
          className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-500"
          required
        >
          <option value="">Select document type</option>
          {documentTypes.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          File *
        </label>
        <input
          type="file"
          onChange={(e) => setFormData(prev => ({ ...prev, file: e.target.files[0] }))}
          className="w-full p-2 border border-border rounded-md bg-background text-foreground"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          required
        />
        <p className="text-xs text-slate-600 mt-1">
          Allowed: PDF, JPG, PNG, DOC, DOCX (Max 10MB)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-900 mb-2">
          Expiry Date (Optional)
        </label>
        <input
          type="date"
          value={formData.expiry_date}
          onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
          className="w-full p-2 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-500"
        />
      </div>

      <div className="flex space-x-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1"
          disabled={uploading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          className="flex-1"
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </div>
    </form>
  );
};

export default DocumentsTab;